import { Trans, useTranslation } from 'next-i18next';
import { useRouter, useSearchParams } from 'next/navigation';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Box, ButtonV2, FelaCSS, SupportMessage } from '@bridebook/ui';
import { IconWarning } from '@bridebook/ui/src/icons';
import { RSVP_FORM_STEPS } from '@bridebook/web/lib/rsvp/constants';
import getWebsiteUrl from '@bridebook/web/lib/rsvp/utils/get-website-url';
import {
  useRsvpAnalytics,
  useRsvpData,
  useRsvpForms,
  useRsvpNavigation,
  useRsvpState,
} from '../../guest-rsvp.context';
import componentStyles from '../substep.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';
import SubmitButtonMobileWrapper from '../../partials/submit-button-mobile-wrapper';
import SubmitButtonDesktopWrapper from '../../partials/submit-button-desktop-wrapper';
import getEventDetailsCaption from '@bridebook/web/lib/rsvp/utils/get-event-details-caption';
import { useMarketDefaultLocale } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market-default-locale';

interface IFirstSubmissionButtonsProps {
  buttonWrapperStyle: FelaCSS;
  continueButtonStyle: FelaCSS;
  onSubmit: ComponentProps<typeof ButtonV2>['onClick'];
  submitPending: boolean;
  sendButtonText: string;
  editButtonText: string;
  onGoBack: () => void;
}

interface IResubmissionButtonsProps {
  buttonWrapperStyle: FelaCSS;
  onGoBack: () => void;
  isRsvpOpen: boolean;
  resubmitButtonText: string;
  goToWebsiteButtonText: string;
  onGoToWebsite: () => void;
}

// Helper function to find question text by question ID
const findQuestionText = (questionId: string, weddingRsvp: any) => {
  if (!weddingRsvp?.questions) return questionId;

  const question = weddingRsvp.questions.find((q: any) => q.id === questionId);
  return question?.question || questionId;
};

const FirstSubmissionButtons: React.FC<IFirstSubmissionButtonsProps> = ({
  buttonWrapperStyle,
  continueButtonStyle,
  onSubmit,
  submitPending,
  sendButtonText,
  editButtonText,
  onGoBack,
}) => (
  <Box style={buttonWrapperStyle}>
    <ButtonV2
      styleWrapper={continueButtonStyle}
      type="submit"
      onClick={onSubmit}
      loading={submitPending}
      disabled={submitPending}>
      {sendButtonText}
    </ButtonV2>
    <ButtonV2 variant="link" onClick={onGoBack}>
      {editButtonText}
    </ButtonV2>
  </Box>
);

const ResubmissionButtons: React.FC<IResubmissionButtonsProps> = ({
  buttonWrapperStyle,
  onGoBack,
  isRsvpOpen,
  resubmitButtonText,
  goToWebsiteButtonText,
  onGoToWebsite,
}) => (
  <Box style={buttonWrapperStyle}>
    <ButtonV2 variant="secondary" onClick={onGoBack} disabled={!isRsvpOpen}>
      {resubmitButtonText}
    </ButtonV2>
    <ButtonV2 variant="primary" onClick={onGoToWebsite}>
      {goToWebsiteButtonText}
    </ButtonV2>
  </Box>
);

const SummaryStep: React.FC = () => {
  const { t } = useTranslation('weddingWebsite');
  const locale = useMarketDefaultLocale();
  const { setStep, setCurrentQuestionIndex, onClose } = useRsvpNavigation();
  const { nameForm, eventsForm, questionsForm, handleSubmit } = useRsvpForms();
  const { rsvpSubmissionStatus, websiteId, isRsvpOpen, isPreview } = useRsvpState();
  const { enrichedEvents, weddingRsvp, weddingWebsite, theme } = useRsvpData();
  const { trackGuestRsvpSent } = useRsvpAnalytics();

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);
  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  const router = useRouter();
  const searchParams = useSearchParams();

  const partners = weddingWebsite.wedding.partners;

  const [submitPending, setSubmitPending] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const errorContainerRef = useRef<HTMLDivElement>(null);

  const onSubmit = useCallback<
    NonNullable<ComponentProps<typeof ButtonV2>['onClick']>
  >(async () => {
    try {
      setSubmitPending(true);
      setHasSubmitError(false);
      await handleSubmit();
      trackGuestRsvpSent();
      setStep('RSVP_FORM.CONFIRMATION');
    } catch (error) {
      setHasSubmitError(true);
      if (errorContainerRef.current) {
        errorContainerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } finally {
      setSubmitPending(false);
    }
  }, [handleSubmit, setStep, trackGuestRsvpSent]);

  const handleGoBack = useCallback(() => {
    setCurrentQuestionIndex(0);
    setStep(RSVP_FORM_STEPS[0]);
  }, [setStep, setCurrentQuestionIndex]);

  const handleGoToWebsite = useCallback(() => {
    router.push(getWebsiteUrl(websiteId, searchParams ?? undefined));
  }, [router, websiteId, searchParams]);

  const handleBackToRsvpForm = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    handleGoBack();
  }, [onClose, handleGoBack]);

  const name = nameForm.getValues('fullName') || '';
  const eventResponses = eventsForm.getValues();

  const { note, ...questionValues } = questionsForm.getValues();

  const questions = useMemo(
    () =>
      Object.entries(questionValues).map(([questionId, answer]) => ({
        id: questionId,
        question: findQuestionText(questionId, weddingRsvp),
        answer:
          typeof answer === 'boolean'
            ? answer
              ? t('rsvp.guest.common.yes')
              : t('rsvp.guest.common.no')
            : answer,
      })),
    [weddingRsvp, questionValues, t],
  );

  const buttonsToRender = isPreview ? (
    <Box style={styles.summary.buttonWrapper}>
      <ButtonV2 onClick={handleBackToRsvpForm}>
        {t('rsvp.preview.summary.cta.backToRsvpForm')}
      </ButtonV2>
    </Box>
  ) : rsvpSubmissionStatus === 'RESUBMISSION' ? (
    <ResubmissionButtons
      buttonWrapperStyle={styles.summary.buttonWrapperResubmission}
      onGoBack={handleGoBack}
      isRsvpOpen={isRsvpOpen}
      resubmitButtonText={t('rsvp.guest.summary.resubmit')}
      goToWebsiteButtonText={t('rsvp.guest.summary.goToWebsite')}
      onGoToWebsite={handleGoToWebsite}
    />
  ) : (
    <FirstSubmissionButtons
      buttonWrapperStyle={styles.summary.buttonWrapper}
      continueButtonStyle={styles.continueButton}
      onSubmit={onSubmit}
      submitPending={submitPending}
      sendButtonText={t('rsvp.guest.summary.cta.send')}
      editButtonText={t('rsvp.guest.summary.cta.edit')}
      onGoBack={handleGoBack}
    />
  );

  return (
    <>
      <Box style={styles.substepWrapper}>
        <Box style={styles.summary.title} as="h1">
          {t('rsvp.guest.summary.title')}
        </Box>
        <Box setRef={errorContainerRef} style={styles.errorMessageWrapper}>
          {hasSubmitError && (
            <SupportMessage
              title={t('rsvp.guest.summary.error.title')}
              icon={<IconWarning width={16} />}
              theme="error">
              {t('rsvp.guest.summary.error.message')}
            </SupportMessage>
          )}
        </Box>
        {rsvpSubmissionStatus === 'RESUBMISSION' && (
          <Box style={styles.alreadySubmittedInfoWrapper}>
            <Trans ns="weddingWebsite" i18nKey="rsvp.guest.summary.alreadySubmittedInfo">
              <p>
                You have already submitted your RSVP.
                <br />
                See your attendance details below.
              </p>
            </Trans>
            {!isRsvpOpen && (
              <Trans
                values={{ partners }}
                ns="weddingWebsite"
                i18nKey="rsvp.guest.common.deadlinePassedInfo">
                <p>The deadline for submitting your RSVP has passed.</p>
                <p>
                  Please contact{' '}
                  <strong>
                    {'{{partners.0}}'} & {'{{partners.1}}'}
                  </strong>{' '}
                  directly if you need to update your response.
                </p>
              </Trans>
            )}
          </Box>
        )}
        <Box style={styles.summary.sectionsWrapper}>
          <Box style={styles.summary.nameWrapper}>
            <Box style={styles.summary.subtitle}>{t('rsvp.guest.summary.name')}</Box>
            <Box>{name}</Box>
          </Box>
          <Box>
            <Box style={styles.summary.subtitle}>{t('rsvp.guest.summary.events')}</Box>
            <Box style={styles.summary.eventsListWrapper}>
              {enrichedEvents.map((event) => (
                <Box key={event.eventId} style={styles.summary.eventListItem}>
                  <Box style={styles.summary.eventListItemDetails}>
                    <Box style={styles.summary.eventName}>{event.data.name}</Box>
                    <Box style={styles.summary.eventCaption}>
                      {getEventDetailsCaption(event, t, locale)}
                    </Box>
                  </Box>
                  <Box style={styles.summary.eventListItemAnswer}>
                    <Box
                      style={styles.summary.eventAnswer(eventResponses[event.eventId] === 'yes')}>
                      {eventResponses[event.eventId] === 'yes'
                        ? t('rsvp.guest.common.yes')
                        : t('rsvp.guest.common.no')}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box style={styles.summary.questionsListWrapper}>
            {questions.map((item) => (
              <Box key={item.id}>
                <Box style={styles.summary.question}>{item.question}:</Box>
                <Box>{item.answer}</Box>
              </Box>
            ))}
          </Box>
          {note && (
            <Box>
              <Box style={styles.summary.subtitle}>{t('rsvp.guest.summary.noteLabel')}</Box>
              <Box style={styles.summary.note}>{note}</Box>
            </Box>
          )}
        </Box>
        <SubmitButtonDesktopWrapper>{buttonsToRender}</SubmitButtonDesktopWrapper>
      </Box>
      <SubmitButtonMobileWrapper>{buttonsToRender}</SubmitButtonMobileWrapper>
    </>
  );
};

export default SummaryStep;
